// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK — NEW-CLIENT appointment brain (Jose 2026-08-06 spec, built 2026-08-07)
 * ==========================================================================
 * A first-timer (name / email / phone NOT already in the client list) booking ANY service gets a
 * longer appointment (30 min) and is flagged so the calendar can label it "New Client — [name]".
 * Tracks new-client acquisition + gives the barber extra time for a first cut/consult.
 *
 * Pure logic + types only — NO React, NO Firebase. Each app loads its own client list and feeds it in.
 * Edit THIS file at repo root and run scripts/sync-shared.sh to propagate. Do NOT edit per-app copies.
 */

/** Minimum length of a new-client appointment (extra time for a first-timer). */
export const NEW_CLIENT_DURATION = 30;

export interface BookingIdentity {
  name?: string;
  phone?: string;
  email?: string;
}

/** Minimal shape we match against — any client record with name/phone/email. */
export interface KnownClientLite {
  name?: string;
  phone?: string;
  email?: string;
}

function normPhone(p?: string): string {
  // Last-10 canonical key: collapse the US +1 so a returning client who books with "+1 973…" still
  // matches a stored 10-digit record and isn't mis-tagged "new" (→ new-client appt). (Jose 2026-10-21)
  const d = (p || '').replace(/\D/g, '');
  return d.length > 10 ? d.slice(-10) : d;
}
function normText(s?: string): string {
  return (s || '').trim().toLowerCase();
}

/** Placeholder identities that aren't a real person to dedupe against. */
const PLACEHOLDER_NAMES = new Set(['', 'walk-in customer', 'walk-in client', 'walk-in', 'guest']);
const PLACEHOLDER_PHONES = new Set(['', '5550000000', '0000000000']);

/**
 * Has this person NOT been here before? True only when we have a real identity (a usable phone,
 * an email, or a real name) AND none of the known clients match by phone, email, or name.
 * Returns false for placeholder walk-ins (no identity → can't tell → don't tag).
 */
export function isNewClient(who: BookingIdentity, known: KnownClientLite[]): boolean {
  const phone = normPhone(who.phone);
  const email = normText(who.email);
  const name = normText(who.name);

  const usablePhone = phone.length >= 7 && !PLACEHOLDER_PHONES.has(phone);
  const usableName = !!name && !PLACEHOLDER_NAMES.has(name);
  if (!usablePhone && !email && !usableName) return false; // no real identity → not tagged

  for (const k of known) {
    if (usablePhone && normPhone(k.phone) === phone) return false;
    if (email && normText(k.email) === email) return false;
    if (usableName && normText(k.name) === name) return false;
  }
  return true;
}

/** The label a new-client appointment reads on the calendar: "New Client — [name]". */
export function newClientLabel(name?: string): string {
  const n = (name || '').trim();
  return n ? `New Client — ${n}` : 'New Client';
}

/**
 * Resolve the duration for a booking given whether the client is new: new clients get at least
 * `minDuration` (never SHORTEN a longer service they picked). `minDuration` is per-shop
 * (BookingRules.newClientMinDuration); defaults to NEW_CLIENT_DURATION when not supplied.
 */
export function durationForBooking(
  baseDuration: number,
  newClient: boolean,
  minDuration: number = NEW_CLIENT_DURATION,
): number {
  return newClient ? Math.max(baseDuration || 0, minDuration || NEW_CLIENT_DURATION) : baseDuration;
}
